/**
 * BackgroundLocationDisclosure
 *
 * Google Play requires a "prominent disclosure" before requesting background location permission.
 * This dialog must be shown BEFORE the OS permission prompt and must include:
 * - The word "location"
 * - The phrase "background", "when the app is closed", or similar
 * - All features that use background location
 * - Two options: Allow and Decline
 *
 * See: https://support.google.com/googleplay/android-developer/answer/9799150
 */

import React from 'react';
import { MapPin, Clock, AlertCircle } from 'lucide-react';

interface BackgroundLocationDisclosureProps {
  isOpen: boolean;
  onAllow: () => void;
  onDecline: () => void;
  language?: 'en' | 'es';
}

export const BackgroundLocationDisclosure: React.FC<BackgroundLocationDisclosureProps> = ({
  isOpen,
  onAllow,
  onDecline,
  language = 'en'
}) => {
  if (!isOpen) return null;

  const t = {
    en: {
      title: 'Background Location Access',
      subtitle: 'Required for automatic job tracking',
      description: 'Bradanna Field needs to access your location in the background (when the app is closed or not in use) for the following features:',
      feature1Title: 'Automatic Job Switching',
      feature1Desc: 'When you drive to a different job site while clocked in, the app will automatically switch your time entry to the correct job after 15 minutes.',
      feature2Title: 'Accurate Time Billing',
      feature2Desc: 'Ensures your hours are billed to the correct job site, even if you forget to manually switch jobs.',
      dataUsage: 'How your location data is used:',
      dataPoint1: 'Location is tracked only while you are clocked in for work',
      dataPoint2: 'Tracking stops immediately when you clock out',
      dataPoint3: 'Location data is used only for job site detection',
      dataPoint4: 'Your location is never shared with third parties',
      allowButton: 'Allow Background Location',
      declineButton: 'Not Now',
      note: 'You can change this later in your device settings.'
    },
    es: {
      title: 'Acceso a Ubicación en Segundo Plano',
      subtitle: 'Requerido para seguimiento automático de trabajos',
      description: 'Bradanna Field necesita acceder a tu ubicación en segundo plano (cuando la app está cerrada o no se usa) para las siguientes funciones:',
      feature1Title: 'Cambio Automático de Trabajo',
      feature1Desc: 'Cuando manejas a un sitio de trabajo diferente mientras estás fichado, la app cambiará automáticamente tu entrada de tiempo al trabajo correcto después de 15 minutos.',
      feature2Title: 'Facturación de Tiempo Precisa',
      feature2Desc: 'Asegura que tus horas se facturen al sitio de trabajo correcto, incluso si olvidas cambiar manualmente de trabajo.',
      dataUsage: 'Cómo se usa tu ubicación:',
      dataPoint1: 'La ubicación se rastrea solo mientras estás fichado',
      dataPoint2: 'El rastreo se detiene inmediatamente cuando fichas salida',
      dataPoint3: 'Los datos de ubicación se usan solo para detectar sitios de trabajo',
      dataPoint4: 'Tu ubicación nunca se comparte con terceros',
      allowButton: 'Permitir Ubicación en Segundo Plano',
      declineButton: 'Ahora No',
      note: 'Puedes cambiar esto después en la configuración de tu dispositivo.'
    }
  }[language];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.85)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 10000,
      padding: '16px'
    }}>
      <div style={{
        backgroundColor: '#1a1a2e',
        borderRadius: '16px',
        maxWidth: '400px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          textAlign: 'center'
        }}>
          <div style={{
            width: '56px',
            height: '56px',
            borderRadius: '50%',
            backgroundColor: 'rgba(59, 130, 246, 0.2)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 12px'
          }}>
            <MapPin size={28} color="#3b82f6" />
          </div>
          <h2 style={{
            color: '#ffffff',
            fontSize: '20px',
            fontWeight: 600,
            margin: '0 0 4px'
          }}>
            {t.title}
          </h2>
          <p style={{
            color: '#94a3b8',
            fontSize: '14px',
            margin: 0
          }}>
            {t.subtitle}
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '20px' }}>
          <p style={{
            color: '#e2e8f0',
            fontSize: '14px',
            lineHeight: 1.6,
            marginBottom: '16px'
          }}>
            {t.description}
          </p>

          {/* Features */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            {/* Feature 1 */}
            <div style={{ display: 'flex', gap: '12px', marginBottom: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <Clock size={18} color="#22c55e" />
              </div>
              <div>
                <h4 style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  margin: '0 0 4px'
                }}>
                  {t.feature1Title}
                </h4>
                <p style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {t.feature1Desc}
                </p>
              </div>
            </div>

            {/* Feature 2 */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <MapPin size={18} color="#3b82f6" />
              </div>
              <div>
                <h4 style={{
                  color: '#ffffff',
                  fontSize: '14px',
                  fontWeight: 600,
                  margin: '0 0 4px'
                }}>
                  {t.feature2Title}
                </h4>
                <p style={{
                  color: '#94a3b8',
                  fontSize: '13px',
                  margin: 0,
                  lineHeight: 1.5
                }}>
                  {t.feature2Desc}
                </p>
              </div>
            </div>
          </div>

          {/* Data Usage */}
          <div style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderRadius: '12px',
            padding: '16px',
            marginBottom: '16px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
              <AlertCircle size={16} color="#94a3b8" />
              <span style={{ color: '#94a3b8', fontSize: '13px', fontWeight: 500 }}>
                {t.dataUsage}
              </span>
            </div>
            <ul style={{
              margin: 0,
              paddingLeft: '20px',
              color: '#e2e8f0',
              fontSize: '13px',
              lineHeight: 1.8
            }}>
              <li>{t.dataPoint1}</li>
              <li>{t.dataPoint2}</li>
              <li>{t.dataPoint3}</li>
              <li>{t.dataPoint4}</li>
            </ul>
          </div>

          <p style={{
            color: '#64748b',
            fontSize: '12px',
            textAlign: 'center',
            marginBottom: '16px'
          }}>
            {t.note}
          </p>
        </div>

        {/* Buttons */}
        <div style={{
          padding: '16px 20px 20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '10px'
        }}>
          <button
            onClick={onAllow}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: '#3b82f6',
              color: '#ffffff',
              fontSize: '15px',
              fontWeight: 600,
              cursor: 'pointer'
            }}
          >
            {t.allowButton}
          </button>
          <button
            onClick={onDecline}
            style={{
              width: '100%',
              padding: '14px',
              borderRadius: '10px',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              backgroundColor: 'transparent',
              color: '#94a3b8',
              fontSize: '15px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
          >
            {t.declineButton}
          </button>
        </div>
      </div>
    </div>
  );
};
